/*
 * Nexo: collection page (sections/nexo-collection.liquid + nexo-collection-head.liquid).
 * Filters, sort, type chips and active-filter chips reload only the two sections through
 * Shopify's Section Rendering API and update the URL, so back/forward and shared links work.
 * "Ver más productos" appends the next page. On phones the filters open as a bottom sheet.
 */

const SHEET_OPEN = 'is-open';

function sectionIdOf(el) {
  const wrapper = el?.closest('.shopify-section');
  return wrapper ? wrapper.id.replace(/^shopify-section-/, '') : '';
}

class NexoCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.controller = null;

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('popstate', this.onPopState);
  }

  /* ---------- building the URL ---------- */

  get form() {
    return this.querySelector('[data-nx-filter-form]');
  }

  paramsFromForm() {
    const params = new URLSearchParams();
    const form = this.form;
    if (form) {
      for (const [key, raw] of new FormData(form)) {
        const value = String(raw).trim();
        if (!value || key === 'filter.v.availability' || key === 'sort_by') continue;
        params.append(key, key.startsWith('filter.v.price') ? value.replace(/[^\d.]/g, '') : value);
      }
    }
    const stock = this.querySelector('[data-nx-stock]');
    if (stock) {
      params.append('filter.v.availability', '1');
      if (!stock.checked) params.append('filter.v.availability', '0');
    }
    const sort = this.querySelector('[data-nx-sort]');
    if (sort && sort.value) params.set('sort_by', sort.value);
    return params;
  }

  apply() {
    const url = `${this.dataset.baseUrl}?${this.paramsFromForm().toString()}`;
    this.load(url);
  }

  /* ---------- events ---------- */

  onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    // Price fields fire "change" on blur or Enter; everything else on toggle
    if (target.closest('[data-nx-filter-form]') || target.matches('[data-nx-sort]')) this.apply();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.querySelector(`[data-nx-sheet].${SHEET_OPEN}`)) this.closeSheet();
  };

  onSubmit = (event) => {
    if (event.target instanceof HTMLFormElement && event.target.matches('[data-nx-filter-form]')) {
      event.preventDefault();
      this.apply();
    }
  };

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const link = target.closest('a[data-nx-filter-link]');
    if (link) {
      event.preventDefault();
      this.load(link.href);
      return;
    }

    const range = target.closest('[data-nx-range]');
    if (range) {
      const block = range.closest('fieldset');
      const [gte, lte] = block ? [...block.querySelectorAll('[data-nx-price]')] : [];
      const isOn = range.getAttribute('aria-pressed') === 'true';
      if (gte) gte.value = isOn ? '' : range.dataset.gte || '';
      if (lte) lte.value = isOn ? '' : range.dataset.lte || '';
      this.apply();
      return;
    }

    if (target.closest('[data-nx-stock-off]')) {
      const stock = this.querySelector('[data-nx-stock]');
      if (stock) {
        stock.checked = false;
        this.apply();
      }
      return;
    }

    const more = target.closest('[data-nx-more-btn]');
    if (more) {
      more.closest('[data-nx-more-group]')?.querySelectorAll('[data-nx-more]').forEach((row) => row.removeAttribute('hidden'));
      more.remove();
      return;
    }

    const next = target.closest('a[data-nx-more-link]');
    if (next) {
      event.preventDefault();
      this.loadMore(next);
      return;
    }

    if (target.closest('[data-nx-sheet-open]')) {
      this.openSheet();
      return;
    }
    if (target.closest('[data-nx-sheet-close]') || target.matches('[data-nx-sheet]')) {
      this.closeSheet();
    }
  };

  // Type chips live in the title section, outside this element
  onDocumentClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-nx-col-head] a[data-nx-filter-link]') : null;
    if (!link) return;
    event.preventDefault();
    this.load(link.href);
  };

  onPopState = () => {
    this.load(window.location.href, { push: false });
  };

  /* ---------- sheet (phones) ---------- */

  openSheet() {
    const sheet = this.querySelector('[data-nx-sheet]');
    if (!sheet) return;
    sheet.classList.add(SHEET_OPEN);
    document.documentElement.classList.add('nx-lock');
    this.lastFocus = document.activeElement;
    sheet.querySelector('[data-nx-sheet-close]')?.focus({ preventScroll: true });
  }

  closeSheet() {
    const sheet = this.querySelector(`[data-nx-sheet].${SHEET_OPEN}`);
    if (!sheet) return;
    sheet.classList.remove(SHEET_OPEN);
    document.documentElement.classList.remove('nx-lock');
    if (this.lastFocus instanceof HTMLElement && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
  }

  /* ---------- loading ---------- */

  sectionsParam() {
    const head = document.querySelector('[data-nx-col-head]');
    const ids = [this.sectionId];
    const headId = sectionIdOf(head);
    if (headId) ids.push(headId);
    return ids;
  }

  async load(url, { push = true } = {}) {
    const target = new URL(url, window.location.origin);
    target.searchParams.delete('page');
    const ids = this.sectionsParam();
    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set('sections', ids.join(','));

    this.controller?.abort();
    this.controller = new AbortController();
    const sheetOpen = Boolean(this.querySelector(`[data-nx-sheet].${SHEET_OPEN}`));
    const focusKey = this.focusKey(document.activeElement);
    this.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(fetchUrl, { signal: this.controller.signal });
      const sections = await res.json();
      this.render(sections[this.sectionId]);
      const headId = ids[1];
      if (headId && sections[headId]) {
        const fresh = new DOMParser().parseFromString(sections[headId], 'text/html').querySelector('[data-nx-col-head]');
        const current = document.querySelector('[data-nx-col-head]');
        if (fresh && current) current.replaceWith(fresh);
      }
      if (push) window.history.pushState({ nexo: true }, '', target.pathname + target.search);
      if (sheetOpen) this.querySelector('[data-nx-sheet]')?.classList.add(SHEET_OPEN);
      this.restoreFocus(focusKey);
      if (!sheetOpen) this.scrollToResults();
    } catch (error) {
      if (error?.name !== 'AbortError') window.location.href = target.toString();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  render(html) {
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('nexo-collection');
    if (!fresh) return;
    this.dataset.stockMode = fresh.dataset.stockMode || '';
    this.replaceChildren(...fresh.childNodes);
  }

  async loadMore(link) {
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');
    const label = link.textContent;
    link.textContent = 'Cargando…';
    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const res = await fetch(url);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const grid = this.querySelector('[data-nx-grid]');
      const items = doc.querySelectorAll('[data-nx-grid] > *');
      const firstNew = items[0];
      grid?.append(...items);
      const freshSeen = doc.querySelector('[data-nx-seen]');
      const seen = this.querySelector('[data-nx-seen]');
      if (freshSeen && seen) seen.replaceWith(freshSeen);
      const focusable = firstNew?.querySelector('a[href]');
      if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
    } catch {
      window.location.href = link.href;
    } finally {
      link.removeAttribute('aria-busy');
      if (document.contains(link)) link.textContent = label;
    }
  }

  focusKey(el) {
    if (!(el instanceof HTMLElement) || !this.contains(el)) return null;
    if (el.matches('input, select')) return { name: el.getAttribute('name'), value: el.getAttribute('value'), data: el.matches('[data-nx-stock]') ? 'stock' : el.matches('[data-nx-sort]') ? 'sort' : '' };
    if (el.matches('[data-nx-range]')) return { range: `${el.dataset.gte}|${el.dataset.lte}` };
    return null;
  }

  restoreFocus(key) {
    if (!key) return;
    let el = null;
    if (key.data === 'stock') el = this.querySelector('[data-nx-stock]');
    else if (key.data === 'sort') el = this.querySelector('[data-nx-sort]');
    else if (key.range) {
      const [gte, lte] = key.range.split('|');
      el = this.querySelector(`[data-nx-range][data-gte="${CSS.escape(gte)}"][data-lte="${CSS.escape(lte)}"]`);
    } else if (key.name) {
      el = this.querySelector(`[name="${CSS.escape(key.name)}"][value="${CSS.escape(key.value || '')}"]`) || this.querySelector(`[name="${CSS.escape(key.name)}"]`);
    }
    if (el instanceof HTMLElement) el.focus({ preventScroll: true });
  }

  scrollToResults() {
    const top = this.getBoundingClientRect().top;
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    if (top >= header) return;
    // The document scrolls (scroll-container.js); the .page-wrapper check is a fallback in case
    // it ever becomes the scroll container again
    const wrapper = document.querySelector('.page-wrapper');
    const scroller = wrapper && wrapper.scrollHeight > wrapper.clientHeight && getComputedStyle(wrapper).overflowY !== 'visible' ? wrapper : null;
    const current = scroller ? scroller.scrollTop : window.scrollY;
    (scroller || window).scrollTo({ top: current + top - header - 12, behavior: 'smooth' });
  }
}

if (!customElements.get('nexo-collection')) customElements.define('nexo-collection', NexoCollection);
